import type { ChangeRecord, RunScope, SceneLogEntry, Track } from '../types.js';

/** Platform-agnostic persistence. Web implements with localStorage; native with secure storage. */
export interface PlotjackStorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export const STORAGE_KEYS = {
  meta: 'plotjack.meta.v1',
  scars: 'plotjack.scars.v1',
  memories: 'plotjack.memories.v1',
  settings: 'plotjack.settings.v1',
  tutorial: 'plotjack.tutorial.v1',
} as const;

export interface TutorialProgress {
  version: 1;
  completed: boolean;
  skipped: boolean;
  lastStepId?: string;
}

const DEFAULT_TUTORIAL: TutorialProgress = {
  version: 1,
  completed: false,
  skipped: false,
};

export async function loadTutorialProgress(storage: PlotjackStorageAdapter): Promise<TutorialProgress> {
  const raw = await storage.getItem(STORAGE_KEYS.tutorial);
  if (!raw) return { ...DEFAULT_TUTORIAL };
  try {
    const parsed = JSON.parse(raw) as TutorialProgress;
    if (parsed.version !== 1) return { ...DEFAULT_TUTORIAL };
    return parsed;
  } catch {
    return { ...DEFAULT_TUTORIAL };
  }
}

export async function saveTutorialProgress(
  storage: PlotjackStorageAdapter,
  progress: TutorialProgress,
): Promise<void> {
  await storage.setItem(STORAGE_KEYS.tutorial, JSON.stringify(progress));
}

export interface SavedContinueRun {
  chosenEndingId: string;
  runScope: RunScope;
  chapterId: string | null;
  sceneIndex: number;
  dread: number;
  tracks: Record<Track, number>;
  deck: string[];
  hand: string[];
  discard: string[];
  drawPile: string[];
  variables: Record<string, string | number | boolean>;
  keepsakes: Record<string, string>;
  callbacks: Record<string, string>;
  history?: ChangeRecord[];
  sceneLog?: SceneLogEntry[];
  metCast?: string[];
  /** First scene not yet played. Older saves lack it and were always written after finishing sceneIndex. */
  resumeIndex?: number;
  savedAt: string;
}

/** The scene a saved run should reopen on: never the one already finished. */
export function resumeSceneIndex(saved: Pick<SavedContinueRun, 'sceneIndex' | 'resumeIndex'>): number {
  return saved.resumeIndex ?? saved.sceneIndex + 1;
}

export interface PersistedMeta {
  totalRuns: number;
  endingsSeen: string[];
  lastRunSummary?: string;
  chaptersCompleted?: string[];
  continueRun?: SavedContinueRun | null;
}

export async function loadMeta(storage: PlotjackStorageAdapter): Promise<PersistedMeta> {
  const raw = await storage.getItem(STORAGE_KEYS.meta);
  if (!raw) {
    return { totalRuns: 0, endingsSeen: [] };
  }
  try {
    return JSON.parse(raw) as PersistedMeta;
  } catch {
    return { totalRuns: 0, endingsSeen: [] };
  }
}

export async function saveMeta(storage: PlotjackStorageAdapter, meta: PersistedMeta): Promise<void> {
  await storage.setItem(STORAGE_KEYS.meta, JSON.stringify(meta));
}

export async function loadScarIds(storage: PlotjackStorageAdapter): Promise<string[]> {
  const raw = await storage.getItem(STORAGE_KEYS.scars);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export async function unlockScar(storage: PlotjackStorageAdapter, scarCardId: string): Promise<string[]> {
  const existing = await loadScarIds(storage);
  if (existing.includes(scarCardId)) return existing;
  const next = [...existing, scarCardId];
  await storage.setItem(STORAGE_KEYS.scars, JSON.stringify(next));
  return next;
}

export async function loadMemories(storage: PlotjackStorageAdapter): Promise<string[]> {
  const raw = await storage.getItem(STORAGE_KEYS.memories);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed.slice(-20) : [];
  } catch {
    return [];
  }
}

export async function pushMemory(storage: PlotjackStorageAdapter, line: string): Promise<void> {
  const mem = await loadMemories(storage);
  mem.push(line);
  const trimmed = mem.slice(-20);
  await storage.setItem(STORAGE_KEYS.memories, JSON.stringify(trimmed));
}
